import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  NOTICE_KEY, SIGNED_OUT, SWITCHED, TOKEN_KEY, whatChanged,
} from '../lib/signInElsewhere';
import { ENDED, ENDED_AT_KEY, SUPPORT_ENDED, endedRecently, noticeFor } from '../lib/sessionEnded';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Consent-login: when a superadmin is acting as a customer user.
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const raw = localStorage.getItem('impersonation');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  // Runs once on mount. `fetchUser` is recreated every render, so listing it would re-fetch endlessly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ⚠️ SIGNED IN OR OUT IN ANOTHER TAB → THIS TAB RELOADS (Rob's answer B1, 16/10/2026).
  // Every request reads the session from shared browser storage, so after a sign-in elsewhere
  // this tab would go on SHOWING the old person while ACTING as the new one — Rob booked Elon
  // off while looking at Harvey's page. `lib/signInElsewhere.js` has the whole story. The
  // `storage` event only ever fires in the OTHER tabs, never the one that signed in.
  // The server ended the session (S1) — say that, not "you signed out in another tab".
  const whySignedOut = (kind) => {
    if (kind !== SIGNED_OUT) return kind;
    let stamp = null;
    try { stamp = localStorage.getItem(ENDED_AT_KEY); } catch { /* no reason to give */ }
    return endedRecently(stamp) ? ENDED : kind;
  };
  const tokenRef = useRef(token);
  tokenRef.current = token;
  useEffect(() => {
    const onStorage = (event) => {
      const theirs = localStorage.getItem(TOKEN_KEY);
      let kind = whatChanged({ key: event.key, mine: tokenRef.current, theirs });
      if (!kind) return;
      kind = whySignedOut(kind);
      try { sessionStorage.setItem(NOTICE_KEY, kind); } catch { /* the reload still happens */ }
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // …and once reloaded, say why — or the page just changing under somebody looks like a fault.
  useEffect(() => {
    let kind = null;
    try {
      kind = sessionStorage.getItem(NOTICE_KEY);
      sessionStorage.removeItem(NOTICE_KEY);
    } catch { /* nothing to say */ }
    if (!kind) return undefined;
    const timer = setTimeout(() => {
      if (kind === SWITCHED) {
        toast.info('You signed in as someone else in another tab, so this tab has switched to them too.');
      } else if (kind === SIGNED_OUT) {
        toast.info('You signed out in another tab.');
      } else if (kind === ENDED || kind === SUPPORT_ENDED) {
        toast.info(noticeFor(kind));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // ⚠️ THE TENANT IS ONLY SET BY login(), WHICH DOES NOT RUN ON A PAGE RELOAD. So on
      // every refresh the app had a user and no tenant — and anything asking "does this
      // company have a name yet?" got "no" and acted on it. That produced an infinite
      // redirect between /welcome and /onboarding, which rendered as a blank screen
      // (Rob, staging, 15/08/2026). `/auth/me` now carries the company name; keep whatever
      // login() already put here and fill in only what is missing.
      setTenant((existing) => (
        existing && existing.company_name
          ? existing
          : { ...(existing || {}), company_name: response.data?.company_name ?? null }
      ));
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try { localStorage.removeItem(ENDED_AT_KEY); } catch { /* fine */ }
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    return response.data;
  };

  const register = async (data) => {
    // New sign-ups are NOT logged in — they must confirm their email first.
    // The backend returns { verification_required, email, message }.
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    return response.data;
  };

  // Store a session from a token response (used after email verification logs
  // the user straight in).
  // The same person, a fresh session: changing your own password signs out every OTHER device
  // and hands this one a new session (S1, Rob's 1A).
  const replaceToken = (newToken) => {
    if (!newToken) return;
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const loginWithToken = ({ access_token, user: userData, tenant: tenantData }) => {
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
  };

  const logout = () => {
    // Tell the server, so this device leaves "Signed in now" and the history says so
    // (DESIGN-security.md). Fire and forget: signing out never waits on, or fails because of, it.
    const _old = localStorage.getItem('token');
    if (_old) {
      try {
        fetch(`${API_URL}/api/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${_old}` } })
          .catch(() => {});
      } catch { /* nothing to do */ }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('sa_token');
    localStorage.removeItem('impersonation');
    setToken(null);
    setUser(null);
    setTenant(null);
    setImpersonation(null);
  };

  // Start acting as a customer user: stash the superadmin token, switch to the
  // short-lived impersonation token (which loads that user via /auth/me).
  const startImpersonation = (impToken, info) => {
    localStorage.setItem('sa_token', localStorage.getItem('token') || '');
    localStorage.setItem('impersonation', JSON.stringify(info || {}));
    localStorage.setItem('token', impToken);
    setImpersonation(info || {});
    setToken(impToken);
  };

  // Exit the support session: restore the superadmin token.
  const stopImpersonation = () => {
    const sa = localStorage.getItem('sa_token');
    localStorage.removeItem('sa_token');
    localStorage.removeItem('impersonation');
    setImpersonation(null);
    if (sa) {
      localStorage.setItem('token', sa);
      setToken(sa);
    } else {
      logout();
    }
  };

  const value = {
    user,
    tenant,
    token,
    loading,
    impersonation,
    login,
    register,
    loginWithToken,
    replaceToken,
    logout,
    startImpersonation,
    stopImpersonation,
    isAuthenticated: !!token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
